import React, { useState } from 'react'
import { X, Star, Image as ImageIcon, Maximize2 } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent, ImageLightbox } from '../ui'

interface VehiclePhoto {
 id: string
 url: string
 isPrimary?: boolean
}

interface VehicleExistingPhotosProps {
 photos: VehiclePhoto[]
 onRemovePhoto: (photoId: string) => void
 onSetPrimaryPhoto?: (photoId: string) => void
}

export const VehicleExistingPhotos: React.FC<VehicleExistingPhotosProps> = ({
 photos,
 onRemovePhoto,
 onSetPrimaryPhoto
}) => {
 const [lightboxOpen, setLightboxOpen] = useState(false)
 const [lightboxIndex, setLightboxIndex] = useState(0)

 const openLightbox = (index: number) => {
 setLightboxIndex(index)
 setLightboxOpen(true)
 }

 if (photos.length === 0) {
 return (
 <Card>
 <CardHeader>
 <CardTitle className="flex items-center">
 <ImageIcon className="h-5 w-5 mr-2" />
 Fotos do Veículo
 </CardTitle>
 </CardHeader>
 <CardContent>
 <div className="text-center py-8">
 <ImageIcon className="h-16 w-16 mx-auto text-gray-300 mb-4" />
 <p className="text-muted-foreground">
 Nenhuma foto cadastrada
 </p>
 </div>
 </CardContent>
 </Card>
 )
 }

 return (
 <Card>
 <CardHeader>
 <CardTitle className="flex items-center justify-between">
 <div className="flex items-center">
 <ImageIcon className="h-5 w-5 mr-2" />
 Fotos do Veículo
 </div>
 <span className="text-sm font-normal text-muted-foreground">
 {photos.length} {photos.length === 1 ? 'foto' : 'fotos'}
 </span>
 </CardTitle>
 </CardHeader>
 <CardContent>
 <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
 {photos.map((photo, index) => (
 <div
 key={photo.id}
 className="relative group"
 >
 <div
 className="aspect-square rounded-lg overflow-hidden border-2 border-border hover:border-accent transition-all cursor-pointer"
 onClick={() => openLightbox(index)}
 >
 {/* Imagem */}
 <img
 src={photo.url}
 alt="Foto do veículo"
 className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
 />

 {/* Overlay de visualização */}
 <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300 flex items-center justify-center">
 <Maximize2 className="h-8 w-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
 </div>
 </div>

 {/* Botão de remover */}
 <button
 type="button"
 onClick={(e) => {
 e.stopPropagation()
 onRemovePhoto(photo.id)
 }}
 className="absolute top-2 right-2 bg-red-600 text-white p-1.5 rounded-full hover:bg-red-700 transition-colors shadow-lg z-10"
 title="Remover foto"
 >
 <X className="h-4 w-4" />
 </button>

 {/* Botão de foto principal */}
 {onSetPrimaryPhoto && (
 <button
 type="button"
 onClick={(e) => {
 e.stopPropagation()
 onSetPrimaryPhoto(photo.id)
 }}
 className={`absolute top-2 left-2 p-1.5 rounded-full transition-colors shadow-lg z-10 ${
 photo.isPrimary
 ? 'bg-yellow-500 text-white'
 : 'bg-card/80 text-muted-foreground hover:bg-yellow-500 hover:text-white'
 }`}
 title={photo.isPrimary ? 'Foto principal' : 'Definir como principal'}
 >
 <Star className={`h-4 w-4 ${photo.isPrimary ? 'fill-current' : ''}`} />
 </button>
 )}

 {/* Badge de foto principal */}
 {photo.isPrimary && (
 <div className="absolute bottom-2 left-2 bg-yellow-500 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1 shadow-lg">
 <Star className="h-3 w-3 fill-current" />
 Principal
 </div>
 )}
 </div>
 ))}
 </div>

 {/* Lightbox */}
 <ImageLightbox
 images={photos.map(p => p.url)}
 currentIndex={lightboxIndex}
 isOpen={lightboxOpen}
 onClose={() => setLightboxOpen(false)}
 onNavigate={setLightboxIndex}
 />

 <p className="text-xs text-muted-foreground mt-4">
 💡 Clique na foto para visualizar em tela cheia. Clique na estrela para definir a foto principal.
 </p>
 </CardContent>
 </Card>
 )
}

